// useEffectivePlan — Pro 권한의 통합 판정.
//
// 3가지 소스를 OR 로 통합:
//   1. Firestore users/{uid}.plan === 'pro'    (RC webhook 으로 동기화된 구독자)
//   2. Firestore users/{uid}.proTrialEndsAt > now (회원가입 시 자동 부여된 14일 trial)
//   3. RC SDK customerInfo.entitlements active  (구매 직후 — webhook 도착 전 fallback)
//
// 사용처: Pricing / Account / CreditModal 등 isPro 판정이 필요한 모든 곳.
// 단순 `useRevenueCatPro` 만 봤다가는 trial 사용자가 free 로 인식돼 paywall
// 노출됨 → 항상 이 hook 사용.

import { useMemo } from 'react';
import { useCredits } from '../services/credits-service.js';
import { useRevenueCatPro } from './useRevenueCatPro.js';

function trialEndsAtToMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value;
  // Firestore Timestamp serialized (e.g. via snapshot) — { seconds, nanoseconds }
  if (typeof value === 'object' && 'seconds' in value) return value.seconds * 1000;
  return 0;
}

export function useEffectivePlan() {
  const credits = useCredits();
  const { isPro: isProViaRC, loading: rcLoading } = useRevenueCatPro();

  return useMemo(() => {
    const isProPaid = credits?.plan === 'pro' || isProViaRC;
    const trialMs = trialEndsAtToMs(credits?.proTrialEndsAt);
    const isProTrial = !isProPaid && trialMs > Date.now();
    const isPro = isProPaid || isProTrial;

    const trialMsLeft = isProTrial ? trialMs - Date.now() : 0;
    const trialDaysLeft = isProTrial
      ? Math.max(1, Math.ceil(trialMsLeft / (24 * 60 * 60 * 1000)))
      : 0;

    return {
      isPro,            // Pro 권한 있음 (구독 or trial)
      isProPaid,        // 진짜 유료 구독 중
      isProTrial,       // 무료 trial 중 (구독 아님)
      trialDaysLeft,    // trial 남은 일수 (없으면 0)
      trialEndsAtMs: isProTrial ? trialMs : 0,
      plan: credits?.plan || 'free',
      loading: credits?.loading || rcLoading,
    };
  }, [credits?.plan, credits?.proTrialEndsAt, credits?.loading, isProViaRC, rcLoading]);
}
